import { useMemo } from "react";
import { useAppSelector } from "../services/hooks";
import { contactPermissionsSelector } from "../services/selectors/user.selectors";
import { TContactPermissions } from "../services/types/auth.types";

/**
 * What the current session may do, area by area. Everything is true for the patient themself;
 * for a contact acting on the patient it follows the permissions the admin set. The server
 * enforces the same rules — this only hides what it would refuse.
 */
export type TContactAccess = {
  calendarView: boolean;
  calendarAction: boolean;
  chatView: boolean;
  chatAction: boolean;
  documentsView: boolean;
  documentsAction: boolean;
  questionnairesView: boolean;
  questionnairesFill: boolean;
  exercisesView: boolean;
};

export const toContactAccess = (permissions: TContactPermissions | null | undefined): TContactAccess => {
  if (!permissions) {
    return {
      calendarView: true,
      calendarAction: true,
      chatView: true,
      chatAction: true,
      documentsView: true,
      documentsAction: true,
      questionnairesView: true,
      questionnairesFill: true,
      exercisesView: true,
    };
  }
  return {
    calendarView: permissions.calendar >= 1,
    calendarAction: permissions.calendar >= 2,
    // The chat's conversations are built from the appointments list, so a contact who may not
    // see the calendar has no chat either, whatever the chat setting says.
    chatView: permissions.chat >= 1 && permissions.calendar >= 1,
    chatAction: permissions.chat >= 2 && permissions.calendar >= 1,
    documentsView: permissions.documents >= 1,
    documentsAction: permissions.documents >= 2,
    questionnairesView: permissions.questionnairesView,
    questionnairesFill: permissions.questionnairesFill,
    exercisesView: permissions.exercisesView,
  };
};

export const useContactAccess = (): TContactAccess => {
  const permissions = useAppSelector(contactPermissionsSelector);
  // Stable between renders, so it is safe in effect dependency lists.
  return useMemo(() => toContactAccess(permissions), [permissions]);
};
